import { api } from './client';

export type BackendLoanStatus =
  | 'PENDING'
  | 'NEED_MORE_INFO'
  | 'APPROVED'
  | 'REJECTED'
  | 'DISBURSED'
  | 'ACTIVE'
  | 'SETTLED'
  | 'OVERDUE'
  | 'CANCELLED';

export type BackendLoanType = 'CASH' | 'MATERIAL';

/** FARMER (default, every pre-existing loan) — borrowerId is a Farmer's _id.
 * HTX — borrowerId is the cooperative's own _id ("tạo khoản vay trực tiếp cho
 * hợp tác xã"); it has no farmer to resolve and must be handled separately
 * everywhere a loan's borrower name/identity is displayed. */
export type BackendLoanBorrowerType = 'FARMER' | 'HTX';

export type BackendReminderThreshold = '7d' | '3d' | '1d' | '12h' | '6h' | '1h' | 'due' | 'overdue';

export type BackendRiskTier = 'A' | 'B' | 'C' | 'D';

export interface BackendLoan {
  _id: string;
  /** `KV-<năm>-NNNN`; missing only until the backend's boot backfill has run. */
  code?: string;
  borrowerId: string;
  borrowerType: BackendLoanBorrowerType;
  htxId: string;
  financingProductId?: string;
  contractId?: string;
  seasonId?: string;
  loanType: BackendLoanType;
  principalVnd: string;
  interestRatePercentPerYear: number;
  termMonths: number;
  purpose?: string;
  materialName?: string;
  materialQuantity?: number;
  materialUnit?: string;
  materialGroup?: string;
  status: BackendLoanStatus;
  // Server-computed at submit/resubmit time from the farmer's Limit lock — never send on submit/resubmit.
  isOverLimit: boolean;
  rejectionReason?: string;
  needMoreInfoNote?: string;
  approvalStage: number;
  pendingApproverRole2: string | null;
  dscrAtDecision?: number;
  carryoverSeasons: number;
  selfFundedRatio: number;
  disbursedAt?: string;
  /** Set by the backend at disbursement (VN calendar, month-end clamped); absent for a loan with no term. */
  dueDate?: string;
  settledAt?: string;
  createdAt: string;
  updatedAt: string;
  [key: string]: unknown;
}

export interface BackendFinancingProduct {
  _id: string;
  code: string;
  name: string;
  interestRatePercentPerYear: number;
  termMonths: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  [key: string]: unknown;
}

export interface BackendLimit {
  _id: string;
  htxId: string;
  farmerId: string;
  tier: BackendRiskTier;
  totalLimitVnd: string;
  usedAmountVnd: string;
  lockedAmountVnd: string;
  lockExpiresAt?: string;
  pendingLoanId?: string;
  [key: string]: unknown;
}

export interface BackendReminderLog {
  _id: string;
  loanId: string;
  farmerId: string;
  threshold: BackendReminderThreshold;
  dueDate: string;
  createdAt: string;
  [key: string]: unknown;
}

export interface LoanListResult {
  items: BackendLoan[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SubmitLoanApplicationPayload {
  farmerId: string;
  htxId: string;
  loanType: BackendLoanType;
  financingProductId?: string;
  seasonId?: string;
  principalVnd: number;
  purpose?: string;
  materialName?: string;
  materialQuantity?: number;
  materialUnit?: string;
  materialGroup?: string;
  selfFundedRatio?: number;
}

export async function submitLoanApplication(payload: SubmitLoanApplicationPayload): Promise<BackendLoan> {
  return api.post<BackendLoan>('/loans', payload);
}

export interface SubmitHtxLoanApplicationPayload {
  htxId: string;
  loanType: BackendLoanType;
  financingProductId?: string;
  principalVnd: number;
  purpose?: string;
  materialName?: string;
  materialQuantity?: number;
  materialUnit?: string;
  materialGroup?: string;
}

/** HTX borrows directly (not on behalf of a farmer) — never auto-approved,
 * always goes through the standard tiered approval queue. */
export async function submitHtxLoanApplication(payload: SubmitHtxLoanApplicationPayload): Promise<BackendLoan> {
  return api.post<BackendLoan>('/loans/htx-application', payload);
}

export async function requestMoreInfo(loanId: string, note: string): Promise<BackendLoan> {
  return api.post<BackendLoan>(`/loans/${loanId}/request-more-info`, { note });
}

export async function resubmitLoan(
  loanId: string,
  payload: { principalVnd?: number; purpose?: string },
): Promise<BackendLoan> {
  return api.post<BackendLoan>(`/loans/${loanId}/resubmit`, payload);
}

/** Farmer's own withdrawal of a still-pending application — the only way to
 * get out of the one-pending-application-at-a-time lock without waiting for
 * an HTX staff member. */
export async function cancelLoan(loanId: string): Promise<BackendLoan> {
  return api.post<BackendLoan>(`/loans/${loanId}/cancel`, {});
}

export async function attachContractToLoan(loanId: string, contractId: string): Promise<BackendLoan> {
  return api.post<BackendLoan>(`/loans/${loanId}/attach-contract`, { contractId });
}

export interface BackendDisbursementTransaction {
  _id: string;
  loanId: string;
  status: 'SUCCESS' | 'FAILED';
  failureReason?: string;
  guardChecks?: Record<string, unknown>;
  [key: string]: unknown;
}

/** The only correct way to disburse a loan — runs the guarded, ledger-backed flow
 * (CONTRACT_ACTIVE check, double-entry posting, banking-adapter payout) via the
 * transactions module. `POST /loans/:id/disburse` also exists in the lending module
 * but is a bare status flip with no guardrails or money movement — do not use it for
 * real disbursement, it exists only as a lending-internal helper. */
export async function disburseLoan(loanId: string): Promise<BackendDisbursementTransaction> {
  return api.post<BackendDisbursementTransaction>(`/transactions/loans/${loanId}/disburse`);
}

export async function settleLoan(loanId: string): Promise<BackendLoan> {
  return api.post<BackendLoan>(`/loans/${loanId}/settle`);
}

export async function flagLoanOverdue(loanId: string): Promise<BackendLoan> {
  return api.post<BackendLoan>(`/loans/${loanId}/flag-overdue`);
}

export async function listLoans(params?: {
  status?: BackendLoanStatus;
  htxId?: string;
  page?: number;
  pageSize?: number;
}): Promise<LoanListResult> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.htxId) query.set('htxId', params.htxId);
  if (params?.page) query.set('page', String(params.page));
  if (params?.pageSize) query.set('pageSize', String(params.pageSize));
  const qs = query.toString();
  return api.get<LoanListResult>(`/loans${qs ? `?${qs}` : ''}`);
}

export async function listLoansByFarmer(farmerId: string): Promise<BackendLoan[]> {
  return api.get<BackendLoan[]>(`/loans/farmers/${farmerId}`);
}

/** The HTX's own direct loans — borrowerType HTX, borrowerId is the htxId
 * itself (see BackendLoanBorrowerType). Distinct from `listLoans({ htxId })`,
 * which is the OPS/ADMIN/CO-only admin listing of every loan under an HTX
 * (farmer loans included) and isn't reachable from the HTX web app. */
export async function listLoansByHtxBorrower(htxId: string): Promise<BackendLoan[]> {
  return api.get<BackendLoan[]>(`/loans/htx/${htxId}`);
}

export async function listLoanReminders(loanId: string): Promise<BackendReminderLog[]> {
  return api.get<BackendReminderLog[]>(`/loans/${loanId}/reminders`);
}

export async function createFinancingProduct(payload: {
  code: string;
  name: string;
  interestRatePercentPerYear: number;
  termMonths: number;
}): Promise<BackendFinancingProduct> {
  return api.post<BackendFinancingProduct>('/financing-products', payload);
}

export async function listFinancingProducts(): Promise<BackendFinancingProduct[]> {
  return api.get<BackendFinancingProduct[]>('/financing-products');
}

export async function getLimitByFarmer(farmerId: string): Promise<BackendLimit | null> {
  return api.get<BackendLimit | null>(`/limits/farmers/${farmerId}`);
}

// ── Limit requests (Xin hạn mức)
//
// The farmer's ask for more limit against one of their contracts. Approving one is
// what performs the grant, so the decide route is staff-only (CO/OPS/ADMIN/
// DEPT_HEAD) — an HTX can watch its members' asks but not decide them.

export type BackendLimitRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface BackendLimitRequest {
  _id: string;
  farmerId: string;
  htxId: string;
  contractId: string;
  requestedAmountVnd: string;
  reason?: string;
  status: BackendLimitRequestStatus;
  decidedAt?: string;
  decisionNote?: string;
  createdAt?: string;
  /** The user id who filed this request — a farmer or the HTX_STAFF that
   * created it "for" a farmer. Only the actor matching this id may cancel
   * (backend enforces it); Feedback 08 B1 found the farmer UI showed a cancel
   * button unconditionally (403 for a request the HTX filed) while the HTX UI
   * had no cancel action at all, even for requests it filed itself. */
  requestedBy?: string;
  /** Only present on the HTX-scoped listing. */
  farmerName?: string;
  contractNo?: string;
  contractValueVnd?: string;
  limitGrantedVnd?: string;
}

export async function createLimitRequest(data: {
  farmerId: string;
  contractId: string;
  requestedAmountVnd: number;
  reason?: string;
}): Promise<BackendLimitRequest> {
  return api.post<BackendLimitRequest>('/limit-requests', data);
}

export async function listLimitRequestsByFarmer(farmerId: string): Promise<BackendLimitRequest[]> {
  return api.get<BackendLimitRequest[]>(`/limit-requests/farmers/${farmerId}`);
}

/** Unlike `listLimitRequestsByFarmer` (a bare array), the htx-scoped route
 * returns `{items, statusCounts}` — `LimitRequestService.listByHtx` computes
 * `statusCounts` across every status regardless of the `status` filter, same
 * pattern as every other admin list endpoint. Unwrapped here so callers keep
 * getting a plain array; `statusCounts` isn't surfaced yet. */
export async function listLimitRequestsByHtx(htxId: string, status?: BackendLimitRequestStatus): Promise<BackendLimitRequest[]> {
  const q = status ? `?status=${status}` : '';
  const res = await api.get<{ items: BackendLimitRequest[]; statusCounts: Record<string, number> }>(`/limit-requests/htx/${htxId}${q}`);
  return res.items;
}

/** Requester's own withdrawal of a still-pending request — the only way out
 * of the one-pending-request-per-contract lock without waiting for CO/OPS/
 * ADMIN/DEPT_HEAD to decide it. */
export async function cancelLimitRequest(requestId: string): Promise<BackendLimitRequest> {
  return api.post<BackendLimitRequest>(`/limit-requests/${requestId}/cancel`, {});
}
